/**
 * MainScorecardPage — /main.
 *
 * quick-261104-ia3 (John 2026-11-04) redesign: "simplify. order should be
 * trended ARR, churn, the pipeline trend … then a monitor of major problems tile
 * and a monitor of major changes tile. then tabs … sales, cs, services, finance.
 * every tile needs to be tight and visually equal."
 *
 * Layout: the Summary tab opens with the exec tiles in John's order — ARR trend,
 * churn, pipeline funnel by $, funnel trend, problems, changes — then the
 * readout. Potential opps are an on-demand audit list (funnel tile button / bar
 * click / problems item). The tab bar switches the department panel (Summary · Sales · Marketing · Customer Success · Services ·
 * Finance · Team · Taxonomy). ?tab= deep-links; ?tab=roi still lands on Finance.
 *
 * Data: GET /api/main/scorecard (existing MAIN-xx series) + GET /api/main/exec
 * (agg_exec_panel) + the dated Rillet constant for ARR / churn.
 */
import { useSearchParams } from 'react-router';

import { AppShell } from '../components/AppShell.js';
import {
  CsPanel,
  ExecBand,
  FinancePanel,
  MarketingPanel,
  SalesPanel,
  ServicesPanel,
  type ExecTab,
} from '../components/exec/ExecLayout.js';
import { FreshnessPill } from '../components/FreshnessPill.js';
import { MainSummaryTab } from '../components/main/MainSummaryTab.js';
import { LEGACY_TAB_ALIASES, MainTabBar, type TabKey } from '../components/main/MainTabBar.js';
import { MainTaxonomyTab } from '../components/main/MainTaxonomyTab.js';
import { MainTeamTab } from '../components/main/MainTeamTab.js';
import { useCroMe } from '../queries/useCroMe.js';
import { useExecPanels } from '../queries/useExecPanels.js';
import { useMainScorecard } from '../queries/useMainScorecard.js';

import type { FreshnessPill as FreshnessPillData } from '@saledash/shared';

const DEFAULT_FRESHNESS: FreshnessPillData = {
  status: 'live',
  lastRefreshedAt: new Date().toISOString(),
};

const TAB_KEYS: readonly TabKey[] = ['summary', 'sales', 'marketing', 'cs', 'services', 'finance', 'team', 'taxonomy'];

function resolveTab(raw: string | null): TabKey {
  if (!raw) return 'summary';
  if ((TAB_KEYS as readonly string[]).includes(raw)) return raw as TabKey;
  return LEGACY_TAB_ALIASES[raw] ?? 'summary';
}

export function MainScorecardPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get('tab'));

  const { data: scorecard, isLoading } = useMainScorecard();
  const exec = useExecPanels();

  const anyMetricFreshness = scorecard ? Object.values(scorecard.metrics)[0]?.freshness : undefined;
  const freshness: FreshnessPillData = anyMetricFreshness
    ? { status: 'live', lastRefreshedAt: anyMetricFreshness.lastRefreshedAt }
    : DEFAULT_FRESHNESS;

  function handleTabChange(tab: TabKey): void {
    setSearchParams({ tab });
  }
  function openDeptTab(tab: ExecTab): void {
    setSearchParams({ tab });
    if (typeof document !== 'undefined') {
      document.getElementById('main-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // CRO members see the CRO scorecard cross-link; everyone else the forecast.
  const { data: meData } = useCroMe();
  const showCroLink = meData?.isCroGroupMember === true;
  const crossLink = showCroLink ? { label: '← CRO scorecard', to: '/' } : { label: 'Forecast ↗', to: '/forecast' };

  return (
    <AppShell
      crossLink={crossLink}
      extraLinks={[{ label: '2027 Plan ↗', to: '/2027-plan' }]}
      freshnessSlot={<FreshnessPill freshness={freshness} />}
    >
      <div className="space-y-5">
        <div id="main-tabs" className="space-y-4 scroll-mt-4">
          <MainTabBar activeTab={activeTab} onChange={handleTabChange} tileCounts={{}} />
          <div role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
            {activeTab === 'summary' && (
              <div className="space-y-6">
                <ExecBand exec={exec.data} scorecard={scorecard} loading={exec.isLoading || isLoading} onOpenTab={openDeptTab} />
                <MainSummaryTab onNavigateTab={handleTabChange} />
              </div>
            )}
            {activeTab === 'sales' && <SalesPanel exec={exec.data} scorecard={scorecard} />}
            {activeTab === 'marketing' && <MarketingPanel scorecard={scorecard} />}
            {activeTab === 'cs' && <CsPanel exec={exec.data} />}
            {activeTab === 'services' && <ServicesPanel exec={exec.data} />}
            {activeTab === 'finance' && <FinancePanel scorecard={scorecard} />}
            {activeTab === 'team' && <MainTeamTab active />}
            {activeTab === 'taxonomy' && <MainTaxonomyTab />}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
